/**
 * Dismissed usage alerts — Zustand + `persist` (localStorage).
 *
 * The app-shell usage banner surfaces every resource that has crossed its
 * warning/limit threshold on EVERY authenticated page. An org that has
 * deliberately maxed out a resource it has no intention of topping up (the
 * classic case: calendar connections) would otherwise be stuck with a
 * permanent red bar, so each alert can be dismissed individually.
 *
 * Dismissal is deliberately PERMANENT — it does not come back when the
 * resource escalates from warning to critical, nor when it heals and breaches
 * again. The only way back is `restoreAll`, exposed on the billing Usage tab.
 *
 * localStorage (not sessionStorage) because the whole point is that it
 * survives reloads and new tabs. Keyed by org so switching tenants never
 * leaks one org's dismissals into another's banner.
 *
 * A store rather than a `localStorage` hook: the banner (app shell) and the
 * restore control (billing page) are mounted in different subtrees and must
 * stay in sync without a reload.
 */
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

/** Shared empty tuple so `useDismissedResourceKeys` keeps a stable identity. */
const NO_DISMISSALS: readonly string[] = [];

interface IUsageAlertDismissalsState {
    /** orgId -> dismissed resource keys. */
    readonly dismissedByOrg: Readonly<Record<string, readonly string[]>>;
    /** Hide one resource's alert for this org, permanently. Idempotent. */
    readonly dismiss: (orgId: string, resourceKey: string) => void;
    /** Un-hide every alert this org has dismissed. */
    readonly restoreAll: (orgId: string) => void;
}

/**
 * Drop anything that isn't a `Record<string, string[]>`. The blob is
 * user-writable (localStorage) and long-lived across releases, so a corrupt or
 * legacy shape must degrade to "nothing dismissed" rather than throw during
 * hydration and take the whole app shell down with it.
 */
function sanitize(value: unknown): Record<string, readonly string[]> {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) return {};

    const result: Record<string, readonly string[]> = {};
    for (const [orgId, keys] of Object.entries(value as Record<string, unknown>)) {
        if (!Array.isArray(keys)) continue;
        result[orgId] = keys.filter((key): key is string => typeof key === 'string');
    }
    return result;
}

export const useUsageAlertDismissalsStore = create<IUsageAlertDismissalsState>()(
    persist(
        (set) => ({
            dismissedByOrg: {},

            dismiss: (orgId, resourceKey) =>
                set((state) => {
                    const current = state.dismissedByOrg[orgId] ?? NO_DISMISSALS;
                    if (current.includes(resourceKey)) return state;
                    return {
                        dismissedByOrg: {
                            ...state.dismissedByOrg,
                            [orgId]: [...current, resourceKey],
                        },
                    };
                }),

            restoreAll: (orgId) =>
                set((state) => {
                    if (!(orgId in state.dismissedByOrg)) return state;
                    const rest = Object.fromEntries(
                        Object.entries(state.dismissedByOrg).filter(([key]) => key !== orgId),
                    );
                    return { dismissedByOrg: rest };
                }),
        }),
        {
            name: 'billing:usage-alerts-dismissed',
            storage: createJSONStorage(() => localStorage),
            partialize: (state) => ({ dismissedByOrg: state.dismissedByOrg }),
            merge: (persisted, current) => ({
                ...current,
                dismissedByOrg: sanitize(
                    (persisted as { dismissedByOrg?: unknown } | undefined)?.dismissedByOrg,
                ),
            }),
        },
    ),
);

/**
 * Resource keys this org has dismissed. Returns a stable empty array when the
 * org has none (or before the org id is known), so consumers don't re-render
 * on every unrelated store write.
 */
export function useDismissedResourceKeys(orgId: string | undefined): readonly string[] {
    return useUsageAlertDismissalsStore((state) =>
        orgId ? (state.dismissedByOrg[orgId] ?? NO_DISMISSALS) : NO_DISMISSALS,
    );
}
